/** TTR, TTT and ARC Continue — the three gestures that pick OBJECTS instead of points.
 *
 * ⚠⚠⚠ **THESE THREE WERE LIVE BUTTONS THAT DID NOTHING, WHICH IS THE ONE THING THIS SHELL IS
 * BUILT NOT TO HAVE.** `handlers.ts` armed all three with tooltips describing what they would
 * do; `Studio.setCircGiven` then printed *"needs the tangency solve — not built yet"* into the
 * command line and returned, and `canContinueArc` was never set by anything, so ARC Continue
 * was permanently greyed saying *"nothing has been drawn yet"* however much you had drawn.
 * A grey button with a reason teaches something. An armed one that prints an apology teaches
 * that the tool is broken.
 *
 * ⚠⚠ **IT IS CLOSED FORM, NOT AN ITERATION, AND THAT IS WHY IT IS SHORT.** The locus of centres
 * of a circle of radius `r` tangent to a LINE is two parallels at ±r; tangent to a CIRCLE of
 * radius `R`, two concentric circles at `R + r` and `|R − r|`. Intersect one locus with the
 * other and every answer falls out at once — up to four of them, which is exactly why AutoCAD
 * asks you to click near the one you meant.
 *
 * ⚠ Everything reduces to the canonical form `do_draw` already accepts — a circle as
 * `[centre, a point on it]`, an arc as `[start, a point it passes through, end]` — so the
 * server never learns that a tangency solve exists. Same rule as `givens.ts`.
 */
import type { Pt } from "./givens";

export type TanObj =
  | { kind: "line"; a: Pt; b: Pt }
  | { kind: "circle"; c: Pt; r: number };

export interface Circ { x: number; y: number; r: number }

const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);

/** A line in normal form: `n·p = d`, with `|n| = 1`. ⚠ Normal form rather than two points
 *  because every step below is a distance from the line, and the normal form IS that distance
 *  — carrying endpoints would mean re-deriving it four times and getting the sign wrong once. */
interface NLine { nx: number; ny: number; d: number }

function normal(o: { a: Pt; b: Pt }): NLine | null {
  const dx = o.b.x - o.a.x, dy = o.b.y - o.a.y;
  const L = Math.hypot(dx, dy);
  if (L < 1e-9) return null;
  const nx = -dy / L, ny = dx / L;
  return { nx, ny, d: nx * o.a.x + ny * o.a.y };
}

/** The geometry of one picked entity, read out of the DISPLAY LIST.
 *
 * ⚠⚠ **FROM THE DISPLAY LIST, NOT FROM A NEW ENDPOINT.** What a drafter picked is what is on
 * screen, and the display list is already the authority on that — it has the layers off, the
 * blocks exploded and the clip applied. Asking the server for the entity again would be a
 * second opinion about the same object, which is the drift this codebase keeps finding.
 *
 * ⚠ A CIRCLE and an ARC arrive FLATTENED, as a run of short chords. Three well-spread points
 * off the run give the centre exactly — a flattened arc is still an arc — and the fit is then
 * CHECKED against every vertex, so a polyline that merely looks round is rejected rather than
 * turned into a circle nobody drew.
 */
export function objOf(dl: { polys: any[] } | null, handle: string, kind: string): TanObj | null {
  if (!dl) return null;
  const runs = dl.polys.filter((r) => r[3] === handle).map((r) => r[2] as number[]);
  if (!runs.length) return null;
  if (kind === "LINE") {
    const flat = runs.find((f) => f.length === 4) ?? runs[0];
    if (flat.length < 4) return null;
    return { kind: "line", a: { x: flat[0], y: flat[1] },
             b: { x: flat[flat.length - 2], y: flat[flat.length - 1] } };
  }
  if (kind !== "CIRCLE" && kind !== "ARC") return null;
  const pts: Pt[] = [];
  for (const f of runs) for (let i = 0; i + 1 < f.length; i += 2) pts.push({ x: f[i], y: f[i + 1] });
  if (pts.length < 3) return null;
  const a = pts[0], b = pts[Math.floor(pts.length / 3)], c = pts[Math.floor((2 * pts.length) / 3)];
  const fit = through(a, b, c);
  if (!fit) return null;
  const tol = Math.max(1e-6, fit.r * 1e-3);
  for (const p of pts) if (Math.abs(dist(p, fit) - fit.r) > tol) return null;
  return { kind: "circle", c: { x: fit.x, y: fit.y }, r: fit.r };
}

function through(a: Pt, b: Pt, c: Pt): Circ | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-12) return null;
  const sa = a.x * a.x + a.y * a.y, sb = b.x * b.x + b.y * b.y, sc = c.x * c.x + c.y * c.y;
  const x = (sa * (b.y - c.y) + sb * (c.y - a.y) + sc * (a.y - b.y)) / d;
  const y = (sa * (c.x - b.x) + sb * (a.x - c.x) + sc * (b.x - a.x)) / d;
  return { x, y, r: Math.hypot(a.x - x, a.y - y) };
}

type Locus = { t: "l"; nx: number; ny: number; d: number } | { t: "c"; x: number; y: number; r: number };

/** Where the centre of a circle of radius `r` tangent to `o` can be. ⚠ TWO loci per object,
 *  always — a circle can sit on either side of a line and either inside or outside another
 *  circle. Returning one is how a TTR silently loses three of its four answers. */
function loci(o: TanObj, r: number): Locus[] {
  if (o.kind === "line") {
    const n = normal(o);
    if (!n) return [];
    return [{ t: "l", nx: n.nx, ny: n.ny, d: n.d + r },
            { t: "l", nx: n.nx, ny: n.ny, d: n.d - r }];
  }
  const out: Locus[] = [{ t: "c", x: o.c.x, y: o.c.y, r: o.r + r }];
  // ⚠ The INTERNAL locus vanishes when the new circle is exactly the size of the old one:
  // |R − r| = 0 is a point, not a circle, and intersecting a zero-radius circle returns
  // nothing rather than the tangent circle through the centre.
  if (Math.abs(o.r - r) > 1e-9) out.push({ t: "c", x: o.c.x, y: o.c.y, r: Math.abs(o.r - r) });
  return out;
}

function meet(A: Locus, B: Locus): Pt[] {
  if (A.t === "l" && B.t === "l") {
    const det = A.nx * B.ny - A.ny * B.nx;
    if (Math.abs(det) < 1e-12) return [];            // parallel: no centre, or infinitely many
    return [{ x: (A.d * B.ny - A.ny * B.d) / det, y: (A.nx * B.d - A.d * B.nx) / det }];
  }
  if (A.t === "c" && B.t === "c") {
    const dx = B.x - A.x, dy = B.y - A.y;
    const D = Math.hypot(dx, dy);
    if (D < 1e-12 || D > A.r + B.r + 1e-9 || D < Math.abs(A.r - B.r) - 1e-9) return [];
    const a = (A.r * A.r - B.r * B.r + D * D) / (2 * D);
    const h2 = A.r * A.r - a * a;
    const h = h2 > 0 ? Math.sqrt(h2) : 0;
    const mx = A.x + (a * dx) / D, my = A.y + (a * dy) / D;
    if (h < 1e-12) return [{ x: mx, y: my }];
    return [{ x: mx + (h * dy) / D, y: my - (h * dx) / D },
            { x: mx - (h * dy) / D, y: my + (h * dx) / D }];
  }
  const L = (A.t === "l" ? A : B) as Extract<Locus, { t: "l" }>;
  const C = (A.t === "c" ? A : B) as Extract<Locus, { t: "c" }>;
  const s = L.d - (L.nx * C.x + L.ny * C.y);         // signed distance centre → line
  if (Math.abs(s) > C.r + 1e-9) return [];
  const foot = { x: C.x + L.nx * s, y: C.y + L.ny * s };
  const h2 = C.r * C.r - s * s;
  if (h2 <= 1e-12) return [foot];
  const h = Math.sqrt(h2);
  return [{ x: foot.x - L.ny * h, y: foot.y + L.nx * h },
          { x: foot.x + L.ny * h, y: foot.y - L.nx * h }];
}

/** Every circle of radius `r` tangent to both objects. ⚠ Up to FOUR, and all of them are
 *  returned — choosing is the caller's job, because only the caller knows where the drafter
 *  clicked. */
export function ttrAll(o1: TanObj, o2: TanObj, r: number): Circ[] {
  if (!(r > 1e-9)) return [];
  const out: Circ[] = [];
  for (const A of loci(o1, r)) {
    for (const B of loci(o2, r)) {
      for (const p of meet(A, B)) out.push({ x: p.x, y: p.y, r });
    }
  }
  return dedupe(out);
}

/** Every circle tangent to three LINES — the incircle and the three excircles.
 *
 * ⚠⚠ **THREE LINES, NOT THREE OBJECTS.** With a circle among them this is the Apollonius
 * problem, which is a genuinely different solve (eight solutions, and the degenerate cases are
 * where it bites). Refusing is better than a circle that touches two of the three.
 */
export function tttAll(l1: TanObj, l2: TanObj, l3: TanObj): Circ[] {
  const ns = [l1, l2, l3].map((o) => (o.kind === "line" ? normal(o) : null));
  if (ns.some((n) => !n)) return [];
  const [A, B, C] = ns as NLine[];
  const out: Circ[] = [];
  // ⚠ `n·p − d = s·r` for each line, with s = ±1 choosing which SIDE of that line the centre
  // sits on. Each pattern is one 3×3 solve, so there is nothing to iterate.
  // ⚠⚠ **ALL EIGHT PATTERNS, NOT FOUR.** Fixing `s1 = 1` and varying the other two looks
  // sufficient — flipping every sign is the same equation with `r` negated — but `r` is then
  // NEGATIVE, and the filter below drops it. Measured on a 3-4-5 triangle: four sign patterns
  // returned two circles, and the incircle was one of the two that were missing.
  for (const s1 of [1, -1]) {
    for (const s2 of [1, -1]) {
      for (const s3 of [1, -1]) {
        const m = [[A.nx, A.ny, -s1], [B.nx, B.ny, -s2], [C.nx, C.ny, -s3]];
        const got = solve3(m, [A.d, B.d, C.d]);
        if (got && got[2] > 1e-9) out.push({ x: got[0], y: got[1], r: got[2] });
      }
    }
  }
  return dedupe(out);
}

function solve3(m: number[][], v: number[]): number[] | null {
  const a = m.map((row, i) => [...row, v[i]]);
  for (let col = 0; col < 3; col++) {
    let piv = col;
    for (let r = col + 1; r < 3; r++) if (Math.abs(a[r][col]) > Math.abs(a[piv][col])) piv = r;
    if (Math.abs(a[piv][col]) < 1e-12) return null;   // two of the lines are parallel
    [a[col], a[piv]] = [a[piv], a[col]];
    for (let r = 0; r < 3; r++) {
      if (r === col) continue;
      const f = a[r][col] / a[col][col];
      for (let c = col; c < 4; c++) a[r][c] -= f * a[col][c];
    }
  }
  return [a[0][3] / a[0][0], a[1][3] / a[1][1], a[2][3] / a[2][2]];
}

function dedupe(cs: Circ[]): Circ[] {
  const out: Circ[] = [];
  for (const c of cs) {
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y) || !(c.r > 1e-9)) continue;
    const tol = Math.max(1e-6, c.r * 1e-6);
    if (out.some((o) => Math.abs(o.x - c.x) < tol && Math.abs(o.y - c.y) < tol
                        && Math.abs(o.r - c.r) < tol)) continue;
    out.push(c);
  }
  return out;
}

/** The one the drafter meant — nearest to where they clicked. ⚠ AutoCAD disambiguates by WHERE
 *  on each object you picked; pdcad selects noun-verb, so the objects are already chosen and
 *  the click that follows is the disambiguation. Same answer, one extra click, and it is a
 *  click rather than a guess. */
export function nearest(cs: Circ[], at: Pt): Circ | null {
  // ⚠⚠ **CENTRE FIRST, RIM AS THE TIE-BREAK, AND THE ORDER IS THE WHOLE POINT.** Rim distance
  // alone ties for all four of a corner's tangent circles — click at (−10,−10) and every one of
  // (±10, ±10) is exactly 10 from the rim, so the answer was whichever came out of the solve
  // first. Centre distance alone ties for two candidates sharing a centre with different radii,
  // which is what a circle-and-line TTR produces. Each metric fails where the other works.
  let best: Circ | null = null, bd = Infinity, br = Infinity;
  for (const c of cs) {
    const d = Math.hypot(at.x - c.x, at.y - c.y);
    const rim = Math.abs(d - c.r);
    const tol = Math.max(1e-6, c.r * 1e-6);
    if (d < bd - tol || (d < bd + tol && rim < br)) { bd = Math.min(bd, d); br = rim; best = c; }
  }
  return best;
}

/** The end point and end DIRECTION of the last thing drawn, for ARC Continue.
 *  ⚠ The direction is the tangent AT THE END, which for an arc is not the chord. */
export interface Cont { end: Pt; dir: number }

export function contOf(kind: string, pts: Pt[]): Cont | null {
  if (pts.length < 2) return null;
  const end = pts[pts.length - 1];
  if (kind !== "ARC") {
    const prev = pts[pts.length - 2];
    const d = Math.atan2(end.y - prev.y, end.x - prev.x);
    return Number.isFinite(d) ? { end, dir: d } : null;
  }
  const c = through(pts[0], pts[1], pts[2]);
  if (!c) return null;
  // ⚠ The tangent is the radius turned 90°, and WHICH 90° depends on the sweep direction. The
  // arc that `_arc_through` builds runs counter-clockwise through the middle point, so the
  // outgoing tangent at the end is the radius turned +90° when it does.
  const cross = (pts[1].x - pts[0].x) * (pts[2].y - pts[0].y)
              - (pts[1].y - pts[0].y) * (pts[2].x - pts[0].x);
  const rad = Math.atan2(end.y - c.y, end.x - c.x);
  return { end, dir: rad + (cross >= 0 ? Math.PI / 2 : -Math.PI / 2) };
}

/** ARC Continue: start where the last one ended, tangent to it, ending at `to`.
 *  Returns the three canonical arc points, or a sentence saying why not. */
export function continueArc(from: Cont, to: Pt): Pt[] | string {
  const s = from.end;
  if (dist(s, to) < 1e-9) return "that is where the last one ended";
  const dx = Math.cos(from.dir), dy = Math.sin(from.dir);
  const nx = -dy, ny = dx;
  const ex = s.x - to.x, ey = s.y - to.y;
  const den = 2 * (nx * ex + ny * ey);
  if (Math.abs(den) < 1e-12) return "that point is straight ahead — a tangent arc to it is a line";
  const t = -(ex * ex + ey * ey) / den;
  const c = { x: s.x + nx * t, y: s.y + ny * t };
  const r = Math.abs(t);
  if (!(r > 1e-9)) return "those points give an arc with no radius";
  const a0 = Math.atan2(s.y - c.y, s.x - c.x);
  const a1 = Math.atan2(to.y - c.y, to.x - c.x);
  // ⚠ Which way round: the counter-clockwise tangent at the start is the radius turned +90°.
  // If the direction we are continuing disagrees with that, the arc sweeps the other way — and
  // taking the CCW sweep regardless draws the complement of the one asked for, which is the
  // same trap `ARC_GIVENS.sed` carries a warning about.
  const ccw = -(s.y - c.y) * dx + (s.x - c.x) * dy > 0;
  const TAU = Math.PI * 2;
  const spanOf = (from0: number, to0: number) => {
    let sp = (to0 - from0) % TAU;
    if (sp <= 0) sp += TAU;
    return sp;
  };
  const [start, span] = ccw ? [a0, spanOf(a0, a1)] : [a1, spanOf(a1, a0)];
  const at = (ai: number): Pt => ({ x: c.x + r * Math.cos(ai), y: c.y + r * Math.sin(ai) });
  return [at(start), at(start + span / 2), at(start + span)];
}
